/*
  Force a collage layout for Instafeed app block after it renders.
  - Applies inline styles with !important so it overrides app inline sizing
  - Handles desktop/tablet/mobile breakpoints
  - Re-applies on feed mutations and on window resize
*/
(function () {
  var BREAKPOINT_TABLET = 1024;
  var BREAKPOINT_MOBILE = 700;
  var MAX_ITEMS = 8; // keep in sync with instafeed-limit.js
  var WRAPPER_SELECTOR = '.instafeed-new-layout-wrapper';
  var ITEM_SELECTOR = '.instafeed-new-layout-item';
  var appliedWrappers = new WeakSet();

  function setImportant(el, prop, value) {
    if (!el || !prop) return;
    try { el.style.setProperty(prop, value, 'important'); } catch (e) {}
  }

  function clearItemSizing(item) {
    if (!item) return;
    setImportant(item, 'width', 'auto');
    setImportant(item, 'height', 'auto');
    setImportant(item, 'clip-path', 'none');
    var img = item.querySelector('img');
    if (img) {
      setImportant(img, 'width', '100%');
      setImportant(img, 'height', '100%');
      setImportant(img, 'object-fit', 'cover');
      setImportant(img, 'display', 'block');
      setImportant(img, 'border-radius', '0');
    }
  }

  function applyDesktopLayout(wrapper, items) {
    // 6 columns, row height 180px
    setImportant(wrapper, 'display', 'grid');
    setImportant(wrapper, 'grid-template-columns', 'repeat(6, 1fr)');
    setImportant(wrapper, 'grid-auto-rows', '180px');
    setImportant(wrapper, 'grid-auto-flow', 'dense');
    setImportant(wrapper, 'gap', '12px');

    // Positions (0-indexed)
    // 1: span 2x2 at columns 1-2
    place(items[0], '1 / span 2', '1 / span 2');
    // 2: span 2x2 at columns 3-4
    place(items[1], '3 / span 2', '1 / span 2');
    // 3: small top-right
    place(items[2], '5 / span 2', '1 / span 1');
    // 4: small below #3
    place(items[3], '5 / span 2', '2 / span 1');
    // 5,6 small bottom-left
    place(items[4], '1 / span 1', '3 / span 1');
    place(items[5], '2 / span 1', '3 / span 1');
    // 7 wide mid-bottom
    place(items[6], '3 / span 2', '3 / span 1');
    // 8 small bottom-right
    place(items[7], '5 / span 2', '3 / span 1');
  }

  function applyTabletLayout(wrapper, items) {
    // 4 columns, row height 140px
    setImportant(wrapper, 'display', 'grid');
    setImportant(wrapper, 'grid-template-columns', 'repeat(4, 1fr)');
    setImportant(wrapper, 'grid-auto-rows', '140px');
    setImportant(wrapper, 'grid-auto-flow', 'dense');
    setImportant(wrapper, 'gap', '12px');

    place(items[0], '1 / span 2', '1 / span 2');
    place(items[1], '3 / span 2', '1 / span 2');
    place(items[2], '1 / span 2', '3 / span 1');
    place(items[3], '3 / span 2', '3 / span 1');
    // Others flow naturally
  }

  function applyMobileLayout(wrapper, items) {
    // 2 columns, row height vw-based
    setImportant(wrapper, 'display', 'grid');
    setImportant(wrapper, 'grid-template-columns', 'repeat(2, 1fr)');
    setImportant(wrapper, 'grid-auto-rows', '46vw');
    setImportant(wrapper, 'grid-auto-flow', 'dense');
    setImportant(wrapper, 'gap', '12px');

    // Reset all to 1x1 first
    items.forEach(function (it) { place(it, 'auto / span 1', 'auto / span 1'); });

    // Top: two tall side-by-side (1,2)
    setImportant(items[0], 'grid-row', 'span 2');
    setImportant(items[1], 'grid-row', 'span 2');
    // Middle: 3,4 small (default)
    // Next: 5 tall left, 6-7 small stacked right (default column flow achieves this)
    setImportant(items[4], 'grid-row', 'span 2');
    // Bottom: 8 full-width
    setImportant(items[7], 'grid-column', '1 / span 2');
  }

  function place(item, col, row) {
    if (!item) return;
    clearItemSizing(item);
    if (col) setImportant(item, 'grid-column', col);
    if (row) setImportant(item, 'grid-row', row);
  }

  function applyLayout(wrapper) {
    if (!wrapper) return;
    var items = Array.prototype.slice.call(wrapper.querySelectorAll(ITEM_SELECTOR)).slice(0, MAX_ITEMS);
    if (!items.length) return;
    // Clear sizes on all we touch
    items.forEach(clearItemSizing);

    var w = window.innerWidth || document.documentElement.clientWidth;
    if (w <= BREAKPOINT_MOBILE) {
      applyMobileLayout(wrapper, items);
    } else if (w <= BREAKPOINT_TABLET) {
      applyTabletLayout(wrapper, items);
    } else {
      applyDesktopLayout(wrapper, items);
    }
  }

  function handleWrapper(wrapper) {
    if (!wrapper) return;
    applyLayout(wrapper);

    if (!appliedWrappers.has(wrapper)) {
      appliedWrappers.add(wrapper);
      var mo = new MutationObserver(function () { applyLayout(wrapper); });
      mo.observe(wrapper, { childList: true, subtree: true });
    }
  }

  function scan() {
    document.querySelectorAll(WRAPPER_SELECTOR).forEach(handleWrapper);
  }

  function boot() {
    scan();
    // Observe document for wrappers added later
    var globalObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        for (var j = 0; j < m.addedNodes.length; j++) {
          var node = m.addedNodes[j];
          if (!(node instanceof HTMLElement)) continue;
          if (node.matches && node.matches(WRAPPER_SELECTOR)) {
            handleWrapper(node);
          } else if (node.querySelector) {
            node.querySelectorAll && node.querySelectorAll(WRAPPER_SELECTOR).forEach(handleWrapper);
          }
        }
      }
    });
    globalObserver.observe(document.documentElement || document.body, { childList: true, subtree: true });
  }

  var resizeTimeout;
  function onResize() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(function () { scan(); }, 150);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  window.addEventListener('load', scan);
  window.addEventListener('resize', onResize);
  document.addEventListener('shopify:section:load', function () { setTimeout(scan, 200); });
})();


